import { useMemo, useReducer } from 'react';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import { loadingReducer, loadingReducerInitialState } from '../../../reducer/loadingReducer';
import {
  CurriculumFlag,
  CurriculumFragment,
  CurriculumInput,
  LessonEditStatus,
  useCreateCurriculumMutation,
  useCurriculaListQuery,
  useDeleteCurriculaMutation,
  useSetCurriculaFlagMutation,
  useTeachingLoadFactorsQuery,
  useUpdateCurriculumMutation,
} from '../../../types/planung-graphql-client-defs';
import { partition } from '../../../utils/arrayFunc';
import { useDeputateLessons } from '../../Lessons/hooks/useDeputateLessons';
import { LessonTableType } from '../../Lessons/TimetableVersion/Table/TimetableVersionLessonsTable';
import { CurriculaTableDataType, CurriculumFormType } from '../types';
import { curriculumResponseToTableType } from '../utils/curriculumResponseToTableType';

type useCurriculumProps = {
  versionUuid: string;
  grade?: string | undefined;
  subjectUuid?: string | undefined;
};

type CurriculumCheckbox = 'teachingLoadEnabled' | 'timetableEnabled' | 'elective' | 'isClassTrip';

const checkboxFlags: Record<CurriculumCheckbox, CurriculumFlag> = {
  teachingLoadEnabled: CurriculumFlag.TeachingLoadEnabled,
  timetableEnabled: CurriculumFlag.TimetableEnabled,
  elective: CurriculumFlag.Elective,
  isClassTrip: CurriculumFlag.IsClassTrip,
};

/** Vollständiger Stand der Sollstunde aus den Formularwerten; die Reihenfolge der Listen ist ihre Reihenfolge. */
export const toCurriculumInput = (values: CurriculumFormType): CurriculumInput => ({
  subjectUuid: values.subject?.uuid ?? '',
  comment: values.comment,
  subTitle: values.subTitle,
  teachingLoadEnabled: values.teachingLoadEnabled,
  timetableEnabled: values.timetableEnabled,
  elective: values.elective,
  isClassTrip: values.isClassTrip,
  persons:
    values.teachers?.map((person) => ({
      description: person.description,
      present: person.present,
      teachingLoad: person.teachingLoad,
    })) ?? [],
  // Ein Eintrag ohne Jahrgang beschreibt keine Klasse und wird nicht gespeichert.
  curriculumGrades:
    values.curriculumGrades
      ?.filter((curriculumClass) => curriculumClass.grades && curriculumClass.grades.length > 0)
      .map((curriculumClass) => ({
        grades: curriculumClass.grades,
        gradeGroupUuids: curriculumClass.gradeGroups?.map((gradeGroup) => gradeGroup.uuid) ?? [],
        groupUuids: curriculumClass.groups?.map((group) => group.uuid) ?? [],
      })) ?? [],
  curriculumUnits:
    values.lessonUnit?.map((curriculumUnit) => ({
      count: curriculumUnit.count != null ? Number(curriculumUnit.count) : null,
      duration: curriculumUnit.duration != null ? Number(curriculumUnit.duration) : null,
      subjectContainerUuid: curriculumUnit.subjectContainerUuid || null,
    })) ?? [],
  roomSupply:
    values.roomSupply
      ?.filter((room) => !!room.uuid)
      .map((room) => ({ roomUuid: room.uuid ?? '', descriptions: room.description })) ?? [],
});

/** Formularwerte einer bestehenden Sollstunde, wie sie Bearbeiten und Duplizieren brauchen. */
export const toCurriculumFormValues = (curriculum: CurriculumFragment): CurriculumFormType => ({
  isClassTrip: curriculum.isClassTrip,
  comment: curriculum.comment,
  subTitle: curriculum.subTitle,
  timetableEnabled: curriculum.timetableEnabled,
  teachingLoadEnabled: curriculum.teachingLoadEnabled,
  elective: curriculum.elective ?? false,
  curriculumGrades: curriculum.curriculumGrades.map((curriculumClass) => ({
    gradeGroups: curriculumClass.gradeGroups,
    grades: curriculumClass.grades,
    groups: curriculumClass.groups,
    uuid: curriculumClass.uuid,
  })),
  // need this name, because we use the LessonUnitForm which names the field;
  // naming it curriculumUnit breaks the form, thus creation / update
  lessonUnit: curriculum.curriculumUnits.map((cu) => ({
    uuid: cu.uuid,
    duration: cu.duration,
    count: cu.count,
    subjectContainerUuid: cu.subjectContainer?.uuid,
    factor: null,
    blocked: false,
    lessonUnitType: cu.subjectContainer?.uuid ? 'epoch' : 'subjectHour',
  })),
  subject: {
    uuid: curriculum.subject.uuid,
    name: curriculum.subject.name,
    subjectHourEnabled: curriculum.subject.isSubjectContainer ? true : curriculum.subject.subjectHourEnabled,
    epochEnabled: curriculum.subject.isSubjectContainer ? false : curriculum.subject.epochEnabled,
    grades: curriculum.subject.grades,
  },
  teachers: curriculum.persons.map((person) => ({
    order: person.order,
    description: person.description,
    teachingLoad: !!person.teachingLoad,
    present: !!person.present,
  })),
  roomSupply: curriculum.roomSupply.map((room) => ({
    uuid: room.roomUuid,
    description: room.descriptions ?? '',
  })),
  curriculumVersionUuid: curriculum.curriculumVersionUuid,
});

export const useCurriculum = ({ versionUuid, grade, subjectUuid }: useCurriculumProps) => {
  const schoolYear = useSelectedSchoolYear();
  const { lessons: lessonsData, deleteLessons: _deleteLessons, refetchLessons } = useDeputateLessons({});

  const [loadingState, dispatch] = useReducer(loadingReducer, loadingReducerInitialState);

  const context = useMemorizedCacheTag('CURRICULA');
  const teachingLoadFactorContext = useMemorizedCacheTag('TEACHINGLOADFACTOR');

  const [, remove] = useDeleteCurriculaMutation();
  const [, create] = useCreateCurriculumMutation();
  const [, update] = useUpdateCurriculumMutation();
  const [, setFlag] = useSetCurriculaFlagMutation();

  const [{ data: queryData }] = useCurriculaListQuery({
    variables: { curriculumVersionUuid: versionUuid, subjectUuid: subjectUuid ?? null },
    context,
  });

  const [{ data: teachingLoadFactors }] = useTeachingLoadFactorsQuery({
    variables: { schoolYearUuid: schoolYear?.uuid ?? '' },
    context: teachingLoadFactorContext,
    pause: !schoolYear,
  });

  // transformations
  const tableData = useMemo(() => {
    return curriculumResponseToTableType({
      curriculumVersionUuid: versionUuid,
      teachingLoadFactors,
      queryCurriculaData: queryData,
      schoolYearUuid: schoolYear?.uuid,
      filterByGrade: grade,
      filterBySubject: subjectUuid,
    });
  }, [teachingLoadFactors, queryData, versionUuid, schoolYear, grade, subjectUuid]);

  const getQueryData = (uuid: string) => {
    return queryData?.curricula.find((c) => c.uuid === uuid);
  };

  const updateCheckboxes = async ({
    type,
    uuids,
    value,
  }: {
    type: CurriculumCheckbox;
    uuids: string[];
    value: boolean;
  }) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    const res = await setFlag({ uuids, flag: checkboxFlags[type], value }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });
    return res;
  };

  // Jahrgangs-Einträge, Einheiten und Lehrer-Platzhalter löscht das Backend mit.
  const deleteCurriculum = async (curricula: CurriculaTableDataType[]) => {
    const uuids = curricula.map((c) => c.uuid);

    dispatch({ type: 'SET_LOADING', uuids: uuids });
    const result = await remove({ uuids }, context);
    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });

    return result;
  };

  const updateCurriculum = async ({
    curriculumUuid,
    values,
  }: {
    curriculumUuid: string;
    values: CurriculumFormType;
  }) => {
    return await update({ uuid: curriculumUuid, input: toCurriculumInput(values) }, context);
  };

  const createCurriculum = async (values: CurriculumFormType) => {
    return create(
      { curriculumVersionUuid: values.curriculumVersionUuid ?? versionUuid, input: toCurriculumInput(values) },
      context,
    );
  };

  const duplicateCurriculum = async (uuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids: uuids });
    const curricula = queryData?.curricula.filter((c) => uuids.includes(c.uuid));
    const createResponses =
      curricula?.map((curriculum) =>
        createCurriculum({ ...toCurriculumFormValues(curriculum), curriculumVersionUuid: versionUuid }),
      ) ?? [];

    dispatch({ type: 'REMOVE_LOADING', uuids: uuids });

    return Promise.all(createResponses);
  };

  const getLessonsForCurriculums = (curriculumUuids: string[]) => {
    refetchLessons();
    const result: LessonTableType[] = [];
    curriculumUuids.forEach((curriculumUuid) => {
      const curriculum = getQueryData(curriculumUuid);
      const lessonUuids = curriculum?.lessons.map((l) => l.uuid);
      if (lessonUuids) {
        const lessons = lessonsData.filter(
          (ld) => lessonUuids.includes(ld.uuid) && ld.schoolYearUuid === schoolYear?.uuid,
        );
        result.push(...lessons);
      }
    });
    return result;
  };

  const deleteLessons = async (curriculumUuids: string[]) => {
    dispatch({ type: 'SET_LOADING', uuids: curriculumUuids });
    let result: { error?: unknown; success?: boolean } = {};
    const lessons = getLessonsForCurriculums(curriculumUuids);
    const [canBeDeleted] = partition(
      lessons,
      (l) => l.editStatus !== LessonEditStatus.Blocked && l.editStatus !== LessonEditStatus.PlacedCards,
    );
    const uuidsToDelete: string[] = canBeDeleted.map((c) => c.uuid);
    if (uuidsToDelete.length > 0) {
      result = await _deleteLessons(uuidsToDelete);
    }

    dispatch({ type: 'REMOVE_LOADING', uuids: curriculumUuids });
    return result;
  };

  return {
    tableData,
    loadingState,
    queryData,
    updateCheckboxes,
    deleteCurriculum,
    updateCurriculum,
    createCurriculum,
    duplicateCurriculum,
    getLessonsForCurriculums,
    getQueryData,
    deleteLessons,
  };
};
